/* eslint-disable max-lines */
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs.tsx';
import PlanDetails from '@pages/back-office/plan-details.tsx';
import ResellerPublications from './reseller-publications';
import PageHeader from '@components/layout/page-header.tsx';
import { Breadcrumbs } from '@components/atoms/ui-kit/breadcrumbs.tsx';
import { PATHS } from '../../router/paths.ts';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Save, FileText, Share2 } from 'lucide-react';
import { useGetPlanBoDetailed } from '@utils/api/back-office/plans-bo/use-get-plan-bo-detailed';
import { useParams } from 'react-router-dom';
import { buildPlansBoBreadcrumbTrail } from '@utils/breadcrump-trails/build-plans-bo-breadcrumb-trail.ts';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PlanBo } from '@utils/types/back-office/update-plan-bo.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  UpdatePlanPayload,
  useUpdatePlan
} from '@utils/api/back-office/plans-bo/use-update-plans-bo.tsx';
import {
  normalizeInteger,
  normalizeNumber
} from '@utils/extensions/number-extensions.ts';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';

export enum PlanResellerTabs {
  PlanDetails = 'Plan Details',
  Publications = 'Reseller Publications'
}

type Params = {
  planId: string;
};

export default function PlanDetailsAndResellerPublications() {
  const { t } = useTranslation();
  const planId = useParams<Params>().planId!;
  const { data: planBo, isLoading } = useGetPlanBoDetailed(planId);

  const [isUpdating, setIsUpdating] = useState(false);

  const { mutate: updatePlan } = useUpdatePlan();

  const validationSchema = Yup.object().shape({
    type: Yup.string().required(t('type-is-required')),
    sku: Yup.string().required(t('sku-is-required')),
    stt_pricing_plan: Yup.number().optional(),
    alignment_pricing_plan: Yup.number().optional(),
    description: Yup.string().optional(),
    batch_enabled: Yup.boolean().optional(),
    realtime_enabled: Yup.boolean().optional(),
    alignment_enabled: Yup.boolean().optional(),
    translation_enabled: Yup.boolean().optional(),
    stt_limit: Yup.number()
      .typeError(t('stt-limit-must-be-integer'))
      .integer(t('stt-limit-must-be-integer'))
      .optional(),
    media_unit_size: Yup.number().optional(),
    translation_cost: Yup.number()
      .typeError(t('translation-cost-must-be-decimal'))
      .optional(),
    translation_characters: Yup.number().optional(),
    translation_character_limit: Yup.number().optional().nullable(),
    monthly_cost: Yup.number()
      .typeError(t('monthly-cost-must-be-decimal'))
      .optional(),
    cost: Yup.number().optional().nullable(),
    minimal_invoice: Yup.number().optional(),
    automatic_training_enabled: Yup.boolean().optional(),
    automatic_training_unit_cost: Yup.number()
      .typeError(t('automatic-training-unit-cost-must-be-decimal'))
      .optional(),
    automatic_training_monthly_cost: Yup.number()
      .typeError(t('automatic-training-monthly-cost-must-be-decimal'))
      .optional(),
    templates_exported_enabled: Yup.boolean().optional(),
    stt_templates_exported_cost: Yup.number().optional(),
    processing_type: Yup.string()
      .oneOf(['BATCH', 'REALTIME', 'OTHER'], t('processing-type-is-invalid'))
      .required(t('processing-type-is-required')),
    // Fair use fields
    is_fair_use: Yup.boolean().optional(),
    fair_use_rate_limit: Yup.number().optional().nullable(),
    automatic_training_unit_limit: Yup.number().optional().nullable(),
    automatic_training_hosting_limit: Yup.number().optional().nullable(),
    custom_model_yearly_limit: Yup.number().optional().nullable(),
    // Exceedance email alerts
    send_exceedance_email: Yup.boolean().optional(),
    exceedance_email_recipients: Yup.string()
      .optional()
      .nullable()
      .when('send_exceedance_email', {
        is: true,
        then: schema =>
          schema.required(t('exceedance-email-recipients-required'))
      }),
    allow_burnt_export: Yup.boolean().optional()
  });

  const {
    register,
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<PlanBo>({
    resolver: yupResolver(validationSchema as Yup.ObjectSchema<PlanBo>),
    defaultValues: {} as PlanBo
  });

  const [isBatchEnabled, setBatchEnabled] = useState(
    planBo?.result.batch_enabled ?? true
  );
  const [isRealtimeEnabled, setRealtimeEnabled] = useState(
    planBo?.result.realtime_enabled ?? true
  );
  const [isAlignmentEnabled, setAlignmentEnabled] = useState(
    planBo?.result.alignment_enabled ?? true
  );
  const [isTranslationEnabled, setTranslationEnabled] = useState(
    planBo?.result.translation_enabled ?? true
  );
  const [isTemplateEnabled, setTemplateEnabled] = useState(
    planBo?.result.templates_exported_enabled ?? true
  );
  const [isAutomaticTrainingEnabled, setAutomaticTrainingEnabled] = useState(
    planBo?.result.automatic_training_enabled ?? true
  );
  const [isBurntExportEnabled, setBurntExportEnabled] = useState(
    planBo?.result.allow_burnt_export ?? false
  );
  const [isAiSummariesEnabled, setAiSummariesEnabled] = useState(
    planBo?.result.ai_summaries_enabled ?? false
  );

  useEffect(() => {
    if (planBo?.result) {
      // Handle both old API format (type.value) and new detailed API format (type as string)
      const typeValue =
        typeof planBo.result.type === 'string'
          ? planBo.result.type
          : (planBo.result.type as { value?: string } | null)?.value || '';
      reset({
        type: typeValue,
        sku: planBo.result.sku || '',
        description: planBo.result.description || '',
        batch_enabled: planBo.result.batch_enabled ?? true,
        realtime_enabled: planBo.result.realtime_enabled ?? true,
        alignment_enabled: planBo.result.alignment_enabled ?? true,
        translation_enabled: planBo.result.translation_enabled ?? true,
        stt_limit: planBo.result.stt_limit ?? 0,
        media_unit_size: planBo.result.media_unit_size ?? 60,
        stt_pricing_plan: (() => {
          const raw = planBo.result.stt_pricing_plan;
          if (typeof raw === 'object' && raw !== null)
            return (raw as { id: number }).id ?? undefined;
          return (raw as number | undefined) ?? undefined;
        })(),
        alignment_pricing_plan: (() => {
          const raw = planBo.result.alignment_pricing_plan;
          if (typeof raw === 'object' && raw !== null)
            return (raw as { id: number }).id ?? undefined;
          return (raw as number | undefined) ?? undefined;
        })(),
        translation_pricing_plan: (() => {
          const tpp = (
            planBo.result as {
              translation_pricing_plan?: number | { id: number } | null;
            }
          ).translation_pricing_plan;
          if (tpp == null) return undefined;
          return typeof tpp === 'object' ? tpp.id : tpp;
        })(),
        translation_cost: planBo.result.translation_cost ?? 0,
        translation_characters: planBo.result.translation_characters ?? 1000,
        translation_character_limit:
          planBo.result.translation_character_limit ?? null,
        monthly_cost: planBo.result.monthly_cost ?? 0,
        cost: planBo.result.cost ?? 0,
        minimal_invoice: planBo.result.minimal_invoice ?? 0,
        automatic_training_enabled:
          planBo.result.automatic_training_enabled ?? false,
        automatic_training_unit_cost:
          planBo.result.automatic_training_unit_cost ?? 0,
        automatic_training_monthly_cost:
          planBo.result.automatic_training_monthly_cost ?? 0,
        stt_templates_exported_cost:
          planBo.result.stt_templates_exported_cost ?? 0,
        templates_exported_enabled:
          planBo.result.templates_exported_enabled ?? true,
        processing_type: planBo.result.processing_type || '',
        // Fair use fields
        is_fair_use: planBo.result.is_fair_use ?? false,
        fair_use_rate_limit: planBo.result.fair_use_rate_limit ?? undefined,
        automatic_training_unit_limit:
          planBo.result.automatic_training_unit_limit ?? undefined,
        automatic_training_hosting_limit:
          planBo.result.automatic_training_hosting_limit ?? undefined,
        custom_model_yearly_limit:
          planBo.result.custom_model_yearly_limit ?? undefined,
        // Exceedance email alerts
        send_exceedance_email: planBo.result.send_exceedance_email ?? false,
        exceedance_email_recipients:
          planBo.result.exceedance_email_recipients ?? '',
        allow_burnt_export: planBo.result.allow_burnt_export ?? false,
        ai_summaries_enabled: planBo.result.ai_summaries_enabled ?? false,
        ai_summaries_monthly_limit:
          planBo.result.ai_summaries_monthly_limit ?? null,
        // Realtime plan-level limits (null = unlimited / all languages)
        realtime_max_concurrent_streams:
          planBo.result.realtime_max_concurrent_streams ?? null,
        realtime_languages: planBo.result.realtime_languages ?? null
      });

      setBatchEnabled(planBo.result.batch_enabled);
      setRealtimeEnabled(planBo.result.realtime_enabled);
      setAlignmentEnabled(planBo.result.alignment_enabled);
      setTranslationEnabled(planBo.result.translation_enabled);
      setTemplateEnabled(planBo.result.templates_exported_enabled);
      setAutomaticTrainingEnabled(planBo.result.automatic_training_enabled);
      setBurntExportEnabled(planBo.result.allow_burnt_export ?? false);
      setAiSummariesEnabled(planBo.result.ai_summaries_enabled ?? false);
    }
  }, [planBo, reset]);

  const tabs: Tab[] = [
    {
      id: 0,
      name: (
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          {PlanResellerTabs.PlanDetails}
        </span>
      ),
      component: (
        <PlanDetails
          register={register}
          control={control}
          setValue={setValue}
          errors={errors}
          isBatchEnabled={isBatchEnabled}
          setBatchEnabled={setBatchEnabled}
          isRealtimeEnabled={isRealtimeEnabled}
          setRealtimeEnabled={setRealtimeEnabled}
          isAlignmentEnabled={isAlignmentEnabled}
          setAlignmentEnabled={setAlignmentEnabled}
          isTranslationEnabled={isTranslationEnabled}
          setTranslationEnabled={setTranslationEnabled}
          isTemplateEnabled={isTemplateEnabled}
          setTemplateEnabled={setTemplateEnabled}
          isAutomaticTrainingEnabled={isAutomaticTrainingEnabled}
          setAutomaticTrainingEnabled={setAutomaticTrainingEnabled}
          isBurntExportEnabled={isBurntExportEnabled}
          setBurntExportEnabled={setBurntExportEnabled}
          isAiSummariesEnabled={isAiSummariesEnabled}
          setAiSummariesEnabled={setAiSummariesEnabled}
        />
      )
    },
    {
      id: 1,
      name: (
        <span className="flex items-center gap-2">
          <Share2 className="h-4 w-4" />
          {PlanResellerTabs.Publications}
        </span>
      ),
      component: <ResellerPublications />
    }
  ];

  const handleUpdateSubmit = handleSubmit(async (updatedValues: PlanBo) => {
    if (!planId) return;

    setIsUpdating(true);

    try {
      const updatedData: Partial<PlanBo> = { ...updatedValues };
      const typeValue = updatedValues.type;

      if (typeValue === 'Perpetual') {
        delete updatedData.monthly_cost;
        delete updatedData.minimal_invoice;
        updatedData.cost = normalizeNumber(updatedValues.cost);
      } else if (typeValue === 'Bundle') {
        delete updatedData.minimal_invoice;
        delete updatedData.cost;
        updatedData.monthly_cost = normalizeNumber(updatedValues.monthly_cost);
      } else if (typeValue === 'Pay as you Go') {
        delete updatedData.monthly_cost;
        delete updatedData.cost;
        updatedData.minimal_invoice = normalizeNumber(
          updatedValues.minimal_invoice
        );
      } else {
        delete updatedData.monthly_cost;
        delete updatedData.minimal_invoice;
        delete updatedData.cost;
      }

      // Only include feature-specific pricing when the feature is enabled
      if (isTranslationEnabled) {
        updatedData.translation_cost = normalizeNumber(
          updatedValues.translation_cost
        );
      } else {
        delete updatedData.translation_cost;
      }

      if (isTemplateEnabled) {
        updatedData.stt_templates_exported_cost = normalizeNumber(
          updatedValues.stt_templates_exported_cost
        );
      } else {
        delete updatedData.stt_templates_exported_cost;
      }

      if (isAutomaticTrainingEnabled) {
        updatedData.automatic_training_unit_cost = normalizeNumber(
          updatedValues.automatic_training_unit_cost
        );
        updatedData.automatic_training_monthly_cost = normalizeNumber(
          updatedValues.automatic_training_monthly_cost
        );
      } else {
        delete updatedData.automatic_training_unit_cost;
        delete updatedData.automatic_training_monthly_cost;
      }

      // Ensure pricing plan fields are integer IDs, not nested objects
      if (isBatchEnabled || isRealtimeEnabled) {
        updatedData.stt_pricing_plan =
          typeof updatedValues.stt_pricing_plan === 'object' &&
          updatedValues.stt_pricing_plan !== null
            ? (updatedValues.stt_pricing_plan as { id: number }).id
            : (updatedValues.stt_pricing_plan ?? null);
      } else {
        delete updatedData.stt_pricing_plan;
      }

      if (isAlignmentEnabled) {
        updatedData.alignment_pricing_plan =
          typeof updatedValues.alignment_pricing_plan === 'object' &&
          updatedValues.alignment_pricing_plan !== null
            ? (updatedValues.alignment_pricing_plan as { id: number }).id
            : (updatedValues.alignment_pricing_plan ?? null);
      } else {
        delete updatedData.alignment_pricing_plan;
      }

      if (isTranslationEnabled) {
        const tppRaw = updatedValues.translation_pricing_plan;
        updatedData.translation_pricing_plan =
          typeof tppRaw === 'object' && tppRaw !== null
            ? (tppRaw as unknown as { id: number }).id
            : (tppRaw ?? null);
      } else {
        delete updatedData.translation_pricing_plan;
      }

      updatedData.stt_limit = normalizeInteger(
        updatedValues.stt_limit ?? undefined
      );
      updatedData.media_unit_size = normalizeInteger(
        updatedValues.media_unit_size
      );

      // Realtime plan-level limits (null = unlimited / all languages)
      if (isRealtimeEnabled) {
        const rawStreams = updatedValues.realtime_max_concurrent_streams;
        updatedData.realtime_max_concurrent_streams =
          rawStreams == null || isNaN(Number(rawStreams))
            ? null
            : Number(rawStreams);
        const rawLangs = updatedValues.realtime_languages;
        if (Array.isArray(rawLangs)) {
          const cleaned = Array.from(
            new Set(
              rawLangs.map(s => String(s).trim().toLowerCase()).filter(Boolean)
            )
          );
          updatedData.realtime_languages = cleaned.length ? cleaned : null;
        } else {
          updatedData.realtime_languages = null;
        }
      } else {
        updatedData.realtime_max_concurrent_streams = null;
        updatedData.realtime_languages = null;
      }

      updatedData.translation_characters = normalizeInteger(
        updatedValues.translation_characters
      );
      updatedData.translation_character_limit =
        updatedValues.translation_character_limit != null
          ? normalizeInteger(updatedValues.translation_character_limit)
          : null;

      // Fair use fields
      updatedData.is_fair_use = updatedValues.is_fair_use ?? false;
      if (updatedValues.is_fair_use) {
        updatedData.fair_use_rate_limit = updatedValues.fair_use_rate_limit
          ? normalizeInteger(updatedValues.fair_use_rate_limit)
          : null;
        updatedData.automatic_training_unit_limit =
          updatedValues.automatic_training_unit_limit
            ? normalizeInteger(updatedValues.automatic_training_unit_limit)
            : null;
        updatedData.automatic_training_hosting_limit =
          updatedValues.automatic_training_hosting_limit
            ? normalizeInteger(updatedValues.automatic_training_hosting_limit)
            : null;
      } else {
        updatedData.fair_use_rate_limit = null;
        updatedData.automatic_training_unit_limit = null;
        updatedData.automatic_training_hosting_limit = null;
      }

      // Custom model yearly training limit (independent of fair use).
      updatedData.custom_model_yearly_limit =
        updatedValues.custom_model_yearly_limit
          ? normalizeInteger(updatedValues.custom_model_yearly_limit)
          : null;

      // Exceedance email alerts
      updatedData.send_exceedance_email =
        updatedValues.send_exceedance_email ?? false;
      if (
        updatedValues.send_exceedance_email &&
        !updatedValues.exceedance_email_recipients?.trim()
      ) {
        toast.error(t('exceedance-email-recipients-required'));
        setIsUpdating(false);
        return;
      }
      updatedData.exceedance_email_recipients =
        updatedValues.send_exceedance_email
          ? updatedValues.exceedance_email_recipients?.trim() || null
          : null;

      updatePlan(
        {
          plan_id: planId,
          data: updatedData as UpdatePlanPayload
        },
        {
          onSuccess: () => {
            setIsUpdating(false);
            toast.success(t('plan-updated-successfully'));
          },
          onError: (error: AxiosError<BaseError>) => {
            setIsUpdating(false);
            const details = error?.response?.data?.detail;
            if (Array.isArray(details)) {
              details.forEach((d: { msg: string }) => toast.error(d.msg));
            } else {
              const fallback =
                error?.response?.data?.error_description ||
                error?.response?.data?.message ||
                error?.message;
              toast.error(
                fallback || t('an-unexpected-error-occurred-please-try-again')
              );
            }
          }
        }
      );
    } catch (error) {
      setIsUpdating(false);
      toast.error(t('an-unexpected-error-occurred-please-try-again'));
    }
  });

  const breadcrumbs = buildPlansBoBreadcrumbTrail(planBo?.result?.sku || '');

  return (
    <div className="bg-app flex-1 flex flex-col gap-4">
      <PageHeader
        title={
          planBo?.result ? (
            <Breadcrumbs
              items={breadcrumbs.slice(0, -1)}
              currentFolderName={'Plan SKU: ' + planBo.result.sku}
              rootLabel="Plans"
              rootPath={PATHS().PLANS}
            />
          ) : (
            t('plan-details')
          )
        }
        caption={t('view-and-update-plan-details-below')}
      >
        <Button
          icon={<Save width={16} />}
          size="small"
          label={t('update')}
          variant="secondary"
          className="px-2"
          onClick={handleUpdateSubmit}
          disabled={isLoading || isUpdating}
        />
      </PageHeader>
      <div className="flex-1 min-h-0">
        <Tabs tabs={tabs} searchParams minHeight="100%" maxHeight="100%" />
      </div>
    </div>
  );
}
